import { BillReceiverType } from "../../../modules/company/admin/organization/userGroups/userGroupsTypes"
import { BillReceiverEndpointMS } from "../createOrder/createOrderTypes"

export enum BillReceiversType {
    PRIVATE_PERSON = "PRIVATE_PERSON",
    PUBLIC_INSTITUTION = "PUBLIC_INSTITUTION",
    PRIVATE_COMPANY = "PRIVATE_COMPANY",
}

export type BillTypeMap<T> = {
    [key in BillReceiversType]: T
}

export enum CountryCode {
    DA = "da",
}

export type SendInvoiceCompany = Readonly<{
    invoiceType: BillReceiverType
    search: string
}>

export type BaseFormValues = Readonly<{
    country: CountryCode
    debtorNumber: string
    addressLine1: string
    addressLine2: string
    postalCode: string
    city: string
    telephone: string
}>

export type PrivatePersonDetailsFormValues = Readonly<
    BaseFormValues & {
        name: string
        email: string
        contactPerson: string
    }
>

export type PublicInstitutionDetailsFormValues = Readonly<
    BaseFormValues & {
        institutionName: string
        vatnumber?: string
        email: string
        contactPerson: string
    }
>

export type PrivateCompanyDetailsFormValues = Readonly<
    BaseFormValues & {
        companyName: string
        email: string
        contactPerson: string
    }
>

export type BillReceiversFormValuesType =
    | PrivatePersonDetailsFormValues
    | PublicInstitutionDetailsFormValues
    | PrivateCompanyDetailsFormValues

export function isPrivatePersonDetails(values: BillReceiversFormValuesType): values is PrivatePersonDetailsFormValues {
    return "name" in values && !("institutionName" in values) && !("companyName" in values)
}

export function isPublicInstitutionDetails(
    values: BillReceiversFormValuesType,
): values is PublicInstitutionDetailsFormValues {
    return "institutionName" in values && !("companyName" in values) && !("name" in values)
}

export function isPrivateCompanyDetails(
    values: BillReceiversFormValuesType,
): values is PrivateCompanyDetailsFormValues {
    return "companyName" in values && !("institutionName" in values) && !("name" in values)
}

export type BillReceiverAddress = {
    countryCode: string
    postalCode: string
    city: string
    addressLines: string[]
}

type Contact = {
    name: string
    email: string
    phoneNumber: string
}

type BillReceiverCommonFields = {
    debitorNo: string
    name: string
    address: BillReceiverAddress
    endpoint: BillReceiverEndpointMS
    isActive: boolean
    onBehalfOf?: {
        userId: number
    }
}

type BillReceiverWithContactDetails = {
    contactDetails: Contact
}

type BillReceiverWithoutContactDetails = {
    contactDetails?: never
}

type BillReceiverWithVatNumber = {
    vatnumber?: string
}

export type PrivatePersonBillReceiver = BillReceiverCommonFields &
    BillReceiverWithoutContactDetails & {
        type: BillReceiversType.PRIVATE_PERSON
    }

export type PublicInstitutionBillReceiver = BillReceiverCommonFields &
    BillReceiverWithContactDetails &
    BillReceiverWithVatNumber & {
        type: BillReceiversType.PUBLIC_INSTITUTION
    }

export type PrivateCompanyBillReceiver = BillReceiverCommonFields &
    BillReceiverWithContactDetails &
    BillReceiverWithVatNumber & {
        type: BillReceiversType.PRIVATE_COMPANY
    }

export type BillReceiver = PrivatePersonBillReceiver | PublicInstitutionBillReceiver | PrivateCompanyBillReceiver

export type BillReceiverEndpointVM = Readonly<{
    kind: "ean" | "email"
    value: string
}>

export type BillReceiverListItemVM = Readonly<{
    id: string
    name: string
    cvr?: string
    endpoint: BillReceiverEndpointVM
    address?: string
}>
